import React, { useCallback } from 'react';
import {
  RichTextField,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
  useStore,
  useUpdate,
} from 'react-admin';

import {
  Box,
  Button,
  Card,
  Typography,
  useTheme,
} from '@mui/material';
import { AddComment, ErrorOutline } from '@mui/icons-material';
import Edit from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ICaseAnalysis } from '../../../../utilities/schemas/caseAnalysis';
import hasAccess from '../../../../utilities/hasAccess';
import actions, { Actions } from '../../../../constants/actions';
import agentModes, { AgentMode } from '../../../../constants/agentModes';
import aiGenerationStatuses from '../../../../constants/aiGenerationStatuses';

export const aiRichTextFieldSx = {
  lineHeight: 1.5,
  '& p': {
    fontSize: '15px',
  },
  '& h1': {
    fontSize: '24px',
  },
  '& h2': {
    fontSize: '22px',
  },
  '& h3': {
    fontSize: '20px',
  },
  '& h4': {
    fontSize: '18px',
  },
  '& h5': {
    fontSize: '16px',
  },
  '& h6': {
    fontSize: '15px',
  },
  '& li': {
    marginY: 2,
    fontSize: '15px',
  },
  '& :first-child': {
    marginTop: 0,
  },
  '& :last-child': {
    marginBottom: 0,
  },
};

const AiAnalysisResult = () => {
  const analysisData = useRecordContext<ICaseAnalysis>();
  const theme = useTheme();

  const { permissions } = usePermissions<Actions[]>();

  const notify = useNotify();
  const [update] = useUpdate();
  const refresh = useRefresh();
  const [, setAgentMode] = useStore<AgentMode>('agentMode', agentModes.FETCHING);
  const isError = analysisData && analysisData.status === aiGenerationStatuses.ERROR;

  const copyTextValue = () => {
    if (isError) return;
    if (analysisData?.result?.original) {
      navigator.clipboard.writeText(analysisData?.result?.original);
      notify('Copied.', { type: 'success' });
    } else {
      notify('No data to copy', { type: 'warning' });
    }
  };

  const handleAddToComments = useCallback(async () => {
    if (!analysisData || isError) return;
    update(
      `cases/${analysisData.caseId}/analysis`,
      {
        id: `${analysisData.id}/add-to-comments`,
        data: {},
      },
      {
        onSuccess: () => {
          notify('Analysis added to comments successfully');
          refresh();
        },
      },
    );
  }, [analysisData, isError, update, notify, refresh]);

  if (!analysisData) return null;

  if (isError) {
    return (
      <Card
        variant="outlined"
        sx={{
          width: '100%',
          minHeight: 400,
          backgroundColor: theme.palette.background.grey,
          alignContent: 'center',
          margin: 0,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          padding={2}
          width="100%"
        >
          <ErrorOutline color="ai" />
          <Typography textAlign="center" justifyContent="center">
            Something went wrong when generating the case analysis.
          </Typography>
          <Typography textAlign="center" justifyContent="center">
            Please try again. If the problem persists,
            please contact our support.
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={4} flexGrow={1} overflow="auto">
      <Card
        variant="outlined"
        sx={{
          margin: 0,
          padding: 4,
          backgroundColor: theme.palette.background.grey,
          flexGrow: 1,
          overflow: 'auto',
          wordBreak: 'break-word',
        }}
      >
        <RichTextField
          sx={aiRichTextFieldSx}
          source={`${analysisData.result.userRevisions.length > 0
            ? `result.userRevisions[${analysisData.result.userRevisions.length - 1}].message`
            : 'result.original'}`}
        />
      </Card>
      <Box
        display="flex"
        gap={2}
        alignItems="center"
        flexWrap="wrap"
      >
        {hasAccess(permissions, actions.ANALYSIS_ADD_TO_COMMENTS) && (
          <Button
            onClick={handleAddToComments}
            variant="contained"
            startIcon={<AddComment />}
          >
            Add to comments
          </Button>
        )}
        <Button
          onClick={copyTextValue}
          variant="outlined"
          startIcon={<ContentCopyIcon />}
        >
          Copy
        </Button>
        {hasAccess(permissions, actions.ANALYSIS_REVISE) && (
          <Button
            onClick={() => setAgentMode(agentModes.EDIT)}
            variant="outlined"
            startIcon={<Edit />}
          >
            Edit
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AiAnalysisResult;
